import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Loader2, RefreshCw, TrendingDown, TrendingUp, Wallet } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatPrice } from "@/lib/format";
import { buildFinanceFromAdRequests } from "@/lib/adminFinanceFallback";

const fmtShortDay = (period) => {
  const p = period.split("-");
  if (p.length === 3) return `${p[1]}.${p[2]}.`;
  return period;
};

const fmtShortMonth = (period) => {
  const p = period.split("-");
  if (p.length === 2) return `${p[0]}. ${p[1]}.`;
  return period;
};

function FinanceChart({ title, data, xFormat }) {
  const chartData = useMemo(
    () => (data || []).map((d) => ({ ...d, label: xFormat(d.period) })),
    [data, xFormat],
  );

  return (
    <div className="glass rounded-2xl border border-white/10 p-4">
      <h3 className="font-heading text-base mb-4">{title}</h3>
      <div className="h-56 w-full" data-testid={`finance-chart-${title}`}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} width={56} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
            <Tooltip
              contentStyle={{ background: "#111318", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }}
              labelStyle={{ color: "#e2e8f0" }}
              formatter={(value, name) => [formatPrice(Number(value)), name]}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="revenue_huf" name="Bevétel" stroke="#22c55e" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="expense_huf" name="Kiadás" stroke="#f87171" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="profit_huf" name="Profit" stroke="#2563eb" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const EMPTY_FIN_FORM = {
  meta_ads_fulfillment_huf: "",
  stripe_fee_percent: "",
  stripe_fee_fixed_huf: "",
  szamlazz_hu_per_invoice_huf: "",
  szamlazz_hu_monthly_huf: "",
};

function applyFinanceForm(source) {
  const s = source?.finance_settings || source || {};
  return {
    meta_ads_fulfillment_huf: String(s.meta_ads_fulfillment_huf ?? ""),
    stripe_fee_percent: String(s.stripe_fee_percent ?? ""),
    stripe_fee_fixed_huf: String(s.stripe_fee_fixed_huf ?? ""),
    szamlazz_hu_per_invoice_huf: String(s.szamlazz_hu_per_invoice_huf ?? ""),
    szamlazz_hu_monthly_huf: String(s.szamlazz_hu_monthly_huf ?? ""),
  };
}

export function AdminFinance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finForm, setFinForm] = useState(EMPTY_FIN_FORM);
  const [savingCost, setSavingCost] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/finance");
      setData(r.data);
      setFinForm(applyFinanceForm(r.data));
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) {
        try {
          const [ads, cfg] = await Promise.all([
            api.get("/admin/ad-requests"),
            api.get("/pricing-config"),
          ]);
          const partial = buildFinanceFromAdRequests(ads.data, cfg.data);
          setData(partial);
          setFinForm(applyFinanceForm(partial));
          toast.warning(
            "A backend még nem ismeri a /admin/finance végpontot — indítsd újra a szervert. Addig csak a Meta kiadások látszanak, a Stripe bevételek nem.",
            { duration: 8000 },
          );
          return;
        } catch {
          /* fall through */
        }
      }
      toast.error(e.response?.data?.detail || "Pénzügyi adatok betöltése sikertelen");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveFinanceSettings = async () => {
    const meta = parseInt(finForm.meta_ads_fulfillment_huf, 10);
    const stripePct = parseFloat(String(finForm.stripe_fee_percent).replace(",", "."));
    const stripeFix = parseInt(finForm.stripe_fee_fixed_huf, 10);
    const szInv = parseInt(finForm.szamlazz_hu_per_invoice_huf, 10);
    const szMo = parseInt(finForm.szamlazz_hu_monthly_huf, 10);
    if ([meta, stripeFix, szInv, szMo].some((n) => Number.isNaN(n) || n < 0) || Number.isNaN(stripePct) || stripePct < 0) {
      toast.error("Ellenőrizd a költség mezőket");
      return;
    }
    setSavingCost(true);
    try {
      await api.patch("/admin/pricing-config", {
        finance: {
          meta_ads_fulfillment_huf: meta,
          stripe_fee_percent: stripePct,
          stripe_fee_fixed_huf: stripeFix,
          szamlazz_hu_per_invoice_huf: szInv,
          szamlazz_hu_monthly_huf: szMo,
        },
      });
      toast.success("Költség beállítások mentve");
      load();
    } catch {
      toast.error("Mentés sikertelen");
    } finally {
      setSavingCost(false);
    }
  };

  const finField = (key, label, props = {}) => (
    <label className="text-xs text-slate-400 block">
      {label}
      <input
        {...props}
        value={finForm[key]}
        onChange={(e) => setFinForm((f) => ({ ...f, [key]: e.target.value }))}
        className="mt-1 block w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-sm text-white"
      />
    </label>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 text-electric animate-spin" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-slate-400 text-sm">Nincs pénzügyi adat (futtasd a backendet).</p>;
  }

  const rows = data.rows || [];

  return (
    <div className="space-y-8" data-testid="admin-finance">
      {data.partial && (
        <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          Részleges nézet: a Stripe bevételek hiányoznak. Állítsd le és indítsd újra a backendet
          (<code className="text-amber-50">uvicorn server:app --reload --port 8000</code> a <code className="text-amber-50">backend</code> mappában),
          majd frissítsd az oldalt.
        </div>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">Aktuális hónap (nulláról)</p>
          <p className="font-heading text-2xl font-light mt-1">{data.month_label}</p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg glass text-sm text-slate-300 hover:text-white"
        >
          <RefreshCw className="w-4 h-4" /> Frissítés
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="glass rounded-2xl p-5 border border-emerald-500/20">
          <div className="flex items-center gap-2 text-emerald-400 text-sm mb-2">
            <TrendingUp className="w-4 h-4" /> Bevétel
          </div>
          <div className="font-heading text-2xl" data-testid="finance-month-revenue">{formatPrice(data.month_revenue_huf)}</div>
          <p className="text-xs text-slate-500 mt-2">Stripe — kredit & előfizetés</p>
        </div>
        <div className="glass rounded-2xl p-5 border border-red-500/20">
          <div className="flex items-center gap-2 text-red-400 text-sm mb-2">
            <TrendingDown className="w-4 h-4" /> Kiadás
          </div>
          <div className="font-heading text-2xl" data-testid="finance-month-expense">{formatPrice(data.month_expense_huf)}</div>
          <p className="text-xs text-slate-500 mt-2">Meta, Stripe díj, Számlázz.hu</p>
        </div>
        <div className="glass rounded-2xl p-5 border border-electric/30">
          <div className="flex items-center gap-2 text-electric text-sm mb-2">
            <Wallet className="w-4 h-4" /> Profit
          </div>
          <div
            className={`font-heading text-2xl ${data.month_profit_huf >= 0 ? "text-white" : "text-red-300"}`}
            data-testid="finance-month-profit"
          >
            {formatPrice(data.month_profit_huf)}
          </div>
          <p className="text-xs text-slate-500 mt-2">Bevétel − kiadás ebben a hónapban</p>
        </div>
      </div>

      <div className="glass rounded-2xl border border-white/10 p-5 space-y-4">
        <h3 className="font-heading text-base">Költségparaméterek (becslés)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {finField("meta_ads_fulfillment_huf", "Meta hirdetés — platform költség / kérés (Ft)", {
            type: "number", min: 0, "data-testid": "finance-meta-cost-input",
          })}
          {finField("stripe_fee_percent", "Stripe — százalék (%)", {
            type: "number", min: 0, step: "0.01", "data-testid": "finance-stripe-percent",
          })}
          {finField("stripe_fee_fixed_huf", "Stripe — fix díj / tranzakció (Ft)", {
            type: "number", min: 0, "data-testid": "finance-stripe-fixed",
          })}
          {finField("szamlazz_hu_per_invoice_huf", "Számlázz.hu — díj / kiállított számla (Ft)", {
            type: "number", min: 0, "data-testid": "finance-szamlazz-invoice",
          })}
          {finField("szamlazz_hu_monthly_huf", "Számlázz.hu — havi csomag / előfizetés (Ft)", {
            type: "number", min: 0, "data-testid": "finance-szamlazz-monthly",
          })}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={savingCost}
            onClick={saveFinanceSettings}
            data-testid="finance-settings-save"
            className="px-4 py-2 rounded-full bg-electric hover:bg-electric-light text-white text-sm disabled:opacity-50"
          >
            {savingCost ? "…" : "Költségek mentése"}
          </button>
          <p className="text-xs text-slate-500 max-w-2xl">
            Minden sikeres Stripe befizetésnél automatikusan számítjuk a Stripe díjat és (ha megadod) a Számlázz.hu
            számlánkénti költséget. A havi Számlázz díj minden naptári hónapban egyszer kerül a kiadások közé.
            Meta kérés: ~{formatPrice(data.meta_ads_service_value_huf)} / {data.meta_ads_credits} kr szolgáltatás.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <FinanceChart title="Napi (30 nap)" data={data.charts?.daily} xFormat={fmtShortDay} />
        <FinanceChart title="Havi (12 hónap)" data={data.charts?.monthly} xFormat={fmtShortMonth} />
        <FinanceChart title="Éves" data={data.charts?.yearly} xFormat={(p) => p} />
      </div>

      <div>
        <h3 className="font-heading text-lg mb-3">Bevételek és kiadások</h3>
        <div className="overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full text-sm" data-testid="finance-ledger-table">
            <thead>
              <tr className="bg-white/[0.04] text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="px-4 py-3">Dátum</th>
                <th className="px-4 py-3">Típus</th>
                <th className="px-4 py-3">Leírás</th>
                <th className="px-4 py-3 text-right">Összeg</th>
                <th className="px-4 py-3 text-right">Profit hatás</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-500">Még nincs tétel.</td>
                </tr>
              ) : (
                rows.map((row) => {
                  const isRev = row.direction === "revenue";
                  const dt = row.occurred_at ? new Date(row.occurred_at).toLocaleString("hu-HU") : "—";
                  return (
                    <tr key={row.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{dt}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full ${
                            isRev ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"
                          }`}
                        >
                          {isRev ? "Bevétel" : "Kiadás"}
                        </span>
                      </td>
                      <td className="px-4 py-3 min-w-[200px]">
                        <div className="font-medium text-slate-200">{row.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{row.detail}</div>
                      </td>
                      <td className={`px-4 py-3 text-right tabular-nums ${isRev ? "text-emerald-300" : "text-red-300"}`}>
                        {isRev ? "+" : "−"}{formatPrice(row.amount_huf)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-300">
                        {row.profit_impact_huf >= 0 ? "+" : ""}{formatPrice(row.profit_impact_huf)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
